import { useEffect, useMemo, useState } from "react";
import { Modal, Table, TableProps, Select, Button, ConfigProvider, theme as antdTheme } from "antd";
import { Input } from "@/components/ui/inputForm";

type ProdutoModalProps = {
  open: boolean;
  produtos: any[];
  /** Produtos já na lista do step: aparecem marcados e não podem ser escolhidos de novo. */
  idsAdicionados: number[];
  unidadeId: number | null;
  /** Recebe todos os produtos marcados quando a seleção é confirmada. */
  onConfirm: (produtos: any[]) => void;
  onCancel: () => void;
};

/** Valor avulso do produto na unidade logada (o preço é por unidade). */
export function valorNaUnidade(produto: any, unidadeId: number | null): number {
  return produto?.unidades?.find((u: any) => u.unidadeId === unidadeId)?.valor ?? 0;
}

/**
 * Valor cobrado quando o produto entra na matrícula por vínculo com uma
 * específica — coluna "Valor Vinculado" do cadastro do produto.
 */
export function valorVinculadoNaUnidade(produto: any, unidadeId: number | null): number {
  return produto?.unidades?.find((u: any) => u.unidadeId === unidadeId)?.valorVinculado ?? 0;
}

export default function ProdutoModal({
  open,
  produtos,
  idsAdicionados,
  unidadeId,
  onConfirm,
  onCancel,
}: ProdutoModalProps) {
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState<string>("todas");
  const [selecionados, setSelecionados] = useState<number[]>([]);

  // Cada abertura começa uma seleção nova.
  useEffect(() => {
    if (open) {
      setSelecionados([]);
      setBusca("");
      setCategoria("todas");
    }
  }, [open]);

  const categorias = useMemo(() => {
    const nomes = Array.from(
      new Set(produtos.map((p) => p.categoriaProdutoNome).filter(Boolean))
    );
    return [{ value: "todas", label: "Todas as categorias" }, ...nomes.map((n) => ({ value: n, label: n }))];
  }, [produtos]);

  const produtosFiltrados = produtos.filter((p) => {
    const casaBusca = p.nome?.toLowerCase().includes(busca.toLowerCase());
    const casaCategoria = categoria === "todas" || p.categoriaProdutoNome === categoria;
    return casaBusca && casaCategoria;
  });

  const confirmar = () => {
    // A ordem segue a da seleção do usuário, não a da tabela.
    const escolhidos = selecionados
      .map((id) => produtos.find((p) => p.id === id))
      .filter(Boolean);
    onConfirm(escolhidos);
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "Produto",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Categoria",
      dataIndex: "categoriaProdutoNome",
      key: "categoriaProdutoNome",
      render: (value) => value || "-",
    },
    {
      title: "Valor",
      key: "valor",
      render: (_, record) =>
        valorNaUnidade(record, unidadeId).toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        }),
    },
    {
      title: "Estoque",
      key: "estoque",
      render: (_, record) => {
        const configuracao = record.unidades?.find((u: any) => u.unidadeId === unidadeId);
        return configuracao ? Number(configuracao.quantidadeEstoque).toLocaleString("pt-BR") : "-";
      },
    },
  ];

  return (
    <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
      <Modal
        title="Pesquisar produto"
        open={open}
        onCancel={onCancel}
        width={840}
        destroyOnClose
        footer={[
          <Button key="cancelar" onClick={onCancel}>
            Cancelar
          </Button>,
          <Button
            key="confirmar"
            type="primary"
            disabled={selecionados.length === 0}
            onClick={confirmar}
          >
            {selecionados.length > 1
              ? `Confirmar (${selecionados.length})`
              : "Confirmar"}
          </Button>,
        ]}
      >
        <div className="flex gap-4 items-end mb-4">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            aria-label="Pesquisar produto"
            className="w-72"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            value={categoria}
            onChange={setCategoria}
            aria-label="Categoria do produto"
            style={{ minWidth: 200 }}
            options={categorias}
          />
        </div>
        <Table
          columns={columns}
          dataSource={produtosFiltrados}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 8 }}
          // A seleção é acumulativa: dá para marcar vários (inclusive trocando de
          // filtro ou de página) e só então confirmar.
          rowSelection={{
            selectedRowKeys: selecionados,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setSelecionados(keys as number[]),
            getCheckboxProps: (record) => ({
              disabled: idsAdicionados.includes(record.id),
              name: record.nome,
            }),
          }}
        />
      </Modal>
    </ConfigProvider>
  );
}
